// Per-phase throughput readout: prefill, thinking (reasoning), generation + TTFT.
import type { RunSummary } from '../api';

interface Props {
  summary: RunSummary | null;
}

function Stat({
  label,
  value,
  unit,
  hint,
  description,
}: {
  label: string;
  value: number | undefined;
  unit: string;
  hint?: string;
  description: string;
}) {
  return (
    <div className="stat" title={description}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {value != null && Number.isFinite(value) ? value.toFixed(1) : '–'}
        <span className="stat-unit">{unit}</span>
      </div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

export default function PhasePanel({ summary }: Props) {
  return (
    <div className="phase-panel">
      <Stat
        label="Prefill"
        value={summary?.meanPrefillTps}
        unit="tok/s"
        hint="Prompt processing"
        description="Prompt-processing throughput: how fast the model ingests the input tokens before it starts producing output."
      />
      <Stat
        label="Thinking"
        value={summary?.meanThinkTps}
        unit="tok/s"
        hint="Reasoning phase"
        description="Reasoning throughput: tokens per second produced during the model's hidden reasoning phase (only for reasoning models; blank otherwise)."
      />
      <Stat
        label="Generation"
        value={summary?.meanGenerateTps}
        unit="tok/s"
        hint="Answer phase"
        description="Answer throughput: tokens per second of the visible response, after any reasoning phase."
      />
      <Stat
        label="TTFT"
        value={summary?.meanTtftMs}
        unit="ms"
        hint="Time to first token"
        description="Time to first token: latency from sending the request until the first token of the response arrives."
      />
      <div
        className="stat"
        title="Matrix cells measured out of the total, plus how many were skipped (input + output + margin above the model's KV limit) or failed."
      >
        <div className="stat-label">Cells</div>
        <div className="stat-value">
          {summary ? summary.cellsMeasured : '–'}
          <span className="stat-unit">/ {summary?.cellsTotal ?? '–'}</span>
        </div>
        <div className="stat-hint">
          {summary ? `${summary.cellsSkipped} skipped · ${summary.cellsErrored} failed` : ''}
        </div>
      </div>

      <div
        className="stat"
        title="Share of the model offloaded to the GPU/Metal accelerator while loaded (Ollama /api/ps: size_vram / size). The rest runs on CPU/RAM."
      >
        <div className="stat-label">GPU offload</div>
        <div className="stat-value">
          {summary?.placement ? summary.placement.gpuPercent : '–'}
          <span className="stat-unit">%</span>
        </div>
        <div className="stat-hint">
          {summary?.placement
            ? `${(summary.placement.vramBytes / 1e9).toFixed(1)} / ${(
                summary.placement.sizeBytes / 1e9
              ).toFixed(1)} GB in VRAM`
            : ''}
        </div>
      </div>
    </div>
  );
}
